import { useField, useFormikContext } from 'formik';
import { BeneficialBusinessOwner, OwnerTreeStructure } from '../../../schema';
import { BUSINESS_OWNERS_FORM_STRING } from '../OwnersTree';
import Button from '../../../../button/Button';
import FormTextField from '../../../../form/FormTextField';
import { useEffect, useState } from 'react';
import { FormSingleSelectField } from '../../../../form/FormSingleSelectField';
import { beneficialOwnersStructureSchema } from '../../../validation';
import { useVerificationContext } from '../../verificationContext';
import UploadField from '../../UploadField';
import { getDirectOwnersPercentageTotal } from '../helpers';

type NewBusinessFormProps = {
    hideLeftBar: boolean;
    fieldname: string;
    parentFieldname: string;
    ownerGuid: string;
    handleDeleteOwner: () => void;
};
export const NewBusinessForm: React.FC<NewBusinessFormProps> = ({
    ownerGuid,
    fieldname,
    parentFieldname,
    hideLeftBar,
    handleDeleteOwner,
}) => {
    const [{ value: parentValue }] = useField<OwnerTreeStructure | OwnerTreeStructure[]>(
        parentFieldname
    );
    const [{ value }, , { setValue: setBusinesses }] = useField<BeneficialBusinessOwner[] | null>(
        BUSINESS_OWNERS_FORM_STRING
    );
    const [, , { setValue: setTreeIsNewValue }] = useField<boolean | undefined>(
        `${fieldname}.isNewNode`
    );
    const [{ value: ownerStartingGuid }] = useField<string>(`${fieldname}.ownersGuid`);
    const [{ value: treeValue }, , { setTouched }] = useField<OwnerTreeStructure>(fieldname);
    const { selectOptions } = useVerificationContext();
    const { values, setFieldTouched } = useFormikContext<any>();

    const handleSetNewItemToFalse = () => {
        setTreeIsNewValue(false);
        /* We always add new entires to the start of the array but this element
           Will have been marked as touched when we add something new we should mark it 
           this index as not touched when we're done adding to avoid showing errors
           on the inputs of a new element */
        setTouched(false);
    };
    const [originalGuid] = useState(ownerStartingGuid);
    const removeOriginalOwner = () => {
        // Remove originally added owner
        // Guid may have changed from dropdown comp hence storing originalGuid
        setBusinesses((value ?? []).filter((item) => item.guid !== originalGuid));
    };
    const handleAddExistingOwner = () => {
        removeOriginalOwner();
        handleSetNewItemToFalse();
    };
    const handleDeleteOwnerExisiting = () => {
        removeOriginalOwner();
        handleDeleteOwner();
    };

    const parentGuid = Array.isArray(parentValue) ? '' : parentValue.ownersGuid;
    const siblingGuids = (Array.isArray(parentValue) ? parentValue : (parentValue.children ?? []))
        .filter((o) => o.bIsBusiness && o.ownersGuid !== parentGuid)
        .map((o) => o.ownersGuid)
        .concat(parentGuid);
    const options = (value ?? [])
        // Filter out existing siblings to not allwow repeats in the same branch level
        .filter((o) => !siblingGuids.includes(o.guid))
        .map((opt, i) => ({
            label: opt.entityName ?? `Business ${i}`,
            value: opt.guid,
        }));
    const hasExistingOwners = options.length > 0;
    const [ownerMethod, setOwnerMethod] = useState<'Existing' | 'New' | null>(
        !hasExistingOwners ? 'New' : null
    );

    const selectedOwnerIndex = (value ?? []).findIndex((o) => o.guid === ownerGuid)!;
    const selectedBusiness =
        selectedOwnerIndex >= 0 ? (value ?? [])[selectedOwnerIndex] : null;

    // New nodes are always inserted/appended at an array index that a previously
    // cancelled add may have already left touched, which would otherwise show that
    // stale item's validation errors immediately on this brand new, untouched form.
    useEffect(() => {
        setFieldTouched(`${fieldname}.percentageOwned`, false, false);
        if (selectedOwnerIndex >= 0) {
            const base = `${BUSINESS_OWNERS_FORM_STRING}[${selectedOwnerIndex}]`;
            setFieldTouched(`${base}.entityName`, false, false);
            setFieldTouched(`${base}.businessFormationDocumentFilename`, false, false);
            setFieldTouched(`${base}.companyUboCertificateOfGoodStandingFilename`, false, false);
            setFieldTouched(`${base}.companyUboW9FormFilename`, false, false);
            setFieldTouched(`${base}.proofOfAddressFilename`, false, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasRequiredDocuments =
        ownerMethod !== 'New' ||
        !!(
            selectedBusiness?.businessFormationDocumentFilename?.trim?.() &&
            selectedBusiness?.companyUboCertificateOfGoodStandingFilename?.trim?.() &&
            selectedBusiness?.companyUboW9FormFilename?.trim?.() &&
            selectedBusiness?.proofOfAddressFilename?.trim?.()
        );

    const siblings = Array.isArray(parentValue) ? parentValue : (parentValue.children ?? []);
    const siblingTotal = getDirectOwnersPercentageTotal(siblings);
    // If this field's own value already exceeds 100, that's already flagged by its own
    // max(100) validation — don't also show the redundant aggregate-total message.
    const ownValueExceeds100 = (treeValue?.percentageOwned ?? 0) > 100;
    // Business owners can never be a controlling party, so 0% is never allowed here.
    const ownValueIsZeroOrLess =
        treeValue?.percentageOwned != null && treeValue.percentageOwned <= 0;
    const exceedsTotalPercentage = siblingTotal > 100 && !ownValueExceeds100;

    const entityNameValid =
        ownerMethod !== 'New' || (selectedBusiness?.entityName ?? '').trim().length > 0;

    const isOwnerStructureValid =
        beneficialOwnersStructureSchema.isValidSync(treeValue) &&
        !exceedsTotalPercentage &&
        entityNameValid &&
        (ownerMethod === 'Existing' ? treeValue.ownersGuid !== originalGuid : true) &&
        (ownerMethod !== 'New' || hasRequiredDocuments);

    const getDocumentId = (internalName: string): number | undefined => {
        const match = selectOptions?.openedDocumentsList?.find(
            (doc) => doc.internalName === internalName
        );
        return match?.id;
    };

    const businessFormationDocumentId = getDocumentId('BBOBusinessFormationDocument');
    const companyUboCertificateOfGoodStandingId = getDocumentId(
        'BBOCompanyUboCertificateOfGoodStanding'
    );
    const companyUboW9FormId = getDocumentId('BBOCompanyUboW9Form');
    const businessProofOfAddressId = getDocumentId('BBOCompanyProofOfAddress');

    return (
        <div className="NewBusinessForm">
            {hideLeftBar && <div aria-hidden="true" className="FloatingHide Full" />}
            {!ownerMethod && (
                <div className="NewIndividualFormRow">
                    <Button
                        onClick={() => setOwnerMethod('New')}
                        type="button"
                        priority="secondary"
                    >
                        Add new business
                    </Button>
                    <Button
                        onClick={() => setOwnerMethod('Existing')}
                        type="button"
                        priority="secondary"
                    >
                        Add existing business
                    </Button>
                </div>
            )}
            {ownerMethod === 'New' && (
                <>
                    <div className="NewBusinessFormRow">
                        <FormTextField
                            label="Legal Name of Entity*"
                            field={`${BUSINESS_OWNERS_FORM_STRING}[${selectedOwnerIndex}].entityName`}
                        />
                        <div className="FloatingErrorField">
                            <FormTextField
                                type="number"
                                label="Percentage of Shares Owned (%)*"
                                field={`${fieldname}.percentageOwned`}
                                hideError
                                className={
                                    exceedsTotalPercentage ||
                                    ownValueExceeds100 ||
                                    ownValueIsZeroOrLess
                                        ? 'ErrorInput'
                                        : ''
                                }
                            />
                            {ownValueExceeds100 && (
                                <div className="ErrorLabel">
                                    Percentage of shares owned cannot exceed 100%
                                </div>
                            )}
                            {ownValueIsZeroOrLess && !ownValueExceeds100 && (
                                <div className="ErrorLabel">
                                    Percentage of shares owned must be greater than 0%
                                </div>
                            )}
                            {!ownValueExceeds100 && !ownValueIsZeroOrLess && exceedsTotalPercentage && (
                                <div className="ErrorLabel">
                                    The combined ownership percentage of beneficial owners cannot
                                    exceed 100%
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="NewBusinessFormRow">
                        <div className="FormBox">
                            <div className="FormLabel">
                                <label>
                                    Business Formation Document
                                    <span>*</span>
                                </label>
                            </div>
                            <UploadField
                                fieldName={`${BUSINESS_OWNERS_FORM_STRING}[${selectedOwnerIndex}].businessFormationDocumentFilename`}
                                displayError={true}
                                jsonField={true}
                                documentId={businessFormationDocumentId}
                            />
                        </div>
                        <div className="FormBox">
                            <div className="FormLabel">
                                <label>
                                    Company UBO Certificate of Good Standing
                                    <span>*</span>
                                </label>
                            </div>
                            <UploadField
                                fieldName={`${BUSINESS_OWNERS_FORM_STRING}[${selectedOwnerIndex}].companyUboCertificateOfGoodStandingFilename`}
                                displayError={true}
                                jsonField={true}
                                documentId={companyUboCertificateOfGoodStandingId}
                            />
                        </div>
                    </div>
                    <div className="NewBusinessFormRow">
                        <div className="FormBox">
                            <div className="FormLabel">
                                <label>
                                    Company UBO Form - W-9 (US) / W-8 (Non-US)
                                    <span>*</span>
                                </label>
                            </div>
                            <UploadField
                                fieldName={`${BUSINESS_OWNERS_FORM_STRING}[${selectedOwnerIndex}].companyUboW9FormFilename`}
                                displayError={true}
                                jsonField={true}
                                documentId={companyUboW9FormId}
                            />
                        </div>
                    </div>
                    <div className="NewBusinessFormRow">
                        <div className="FormBox">
                            <div className="FormLabel">
                                <label>
                                    Proof of Address
                                    <span>*</span>
                                </label>
                            </div>
                            <UploadField
                                fieldName={`${BUSINESS_OWNERS_FORM_STRING}[${selectedOwnerIndex}].proofOfAddressFilename`}
                                displayError={true}
                                jsonField={true}
                                documentId={businessProofOfAddressId}
                            />
                        </div>
                    </div>
                    <div className="NewBusinessFormRow">
                        <Button onClick={handleDeleteOwner} type="button" priority="secondary">
                            Cancel
                        </Button>
                        <Button
                            disabled={!isOwnerStructureValid}
                            onClick={handleSetNewItemToFalse}
                            type="button"
                        >
                            Add
                        </Button>
                    </div>
                </>
            )}
            {ownerMethod === 'Existing' && (
                <>
                    <FormSingleSelectField<any>
                        options={options}
                        label="Choose from existing businesses"
                        fieldName={`${fieldname}.ownersGuid`}
                    />
                    <div className="FloatingErrorField">
                        <FormTextField
                            type="number"
                            label="Percentage of Shares Owned (%)*"
                            field={`${fieldname}.percentageOwned`}
                            hideError
                            className={
                                exceedsTotalPercentage || ownValueExceeds100 || ownValueIsZeroOrLess
                                    ? 'ErrorInput'
                                    : ''
                            }
                        />
                        {ownValueExceeds100 && (
                            <div className="ErrorLabel">
                                Percentage of shares owned cannot exceed 100%
                            </div>
                        )}
                        {ownValueIsZeroOrLess && !ownValueExceeds100 && (
                            <div className="ErrorLabel">
                                Percentage of shares owned must be greater than 0%
                            </div>
                        )}
                        {!ownValueExceeds100 && !ownValueIsZeroOrLess && exceedsTotalPercentage && (
                            <div className="ErrorLabel">
                                The combined ownership percentage of beneficial owners cannot
                                exceed 100%
                            </div>
                        )}
                    </div>
                    <div className="NewIndividualFormRow AddSave">
                        <Button
                            onClick={handleDeleteOwnerExisiting}
                            type="button"
                            priority="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!isOwnerStructureValid}
                            onClick={handleAddExistingOwner}
                            type="button"
                        >
                            Add
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
